import React, { useState, useEffect } from "react";

import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Switch,
  Platform,
} from "react-native";

import {
  convert24hToAMPM,
  setStorageItem,
  getStorageItem,
} from "../../../helpers/utils";

import AutoTrackPickerDialog from "../common/AutoTrackPickerDialog";
import TimePickerDialog from "../common/TimePickerDialog";
import IntervalDurationDialog from "../common/IntervalDurationDialog";
import TimezoneDialog from "../common/TimezoneDialog";
import Header from "../common/header/index";
import appStyles from "../../appStyles";
import AppTheme from "../AppTheme";
import BlacklistElements from "./elements/BlacklistElements";
import SettingsHelper from "./SettingsHelper";
import LoadingIndicator from "../common/LoadingIndicator";

const thumbColor = "#239c9d";

export default function Preferences({ controller }) {
  const [settings, setSettings] = useState({});
  const [useDeviceTz, setUseDeviceTz] = useState();
  const [isNotificationDialogOpen, setIsNotificationDialogOpen] =
    useState(false);
  const [isAutoTrackDialogOpen, setIsAutoTrackDialogOpen] = useState(false);
  const [isIntervalDialogOpen, setIsIntervalDialogOpen] = useState(false);
  const [isTimezoneDialogOpen, setIsTimezoneDialogOpen] = useState(false);
  const [timePickerManualMode, setTimePickerManualMode] = useState(false);
  const [showElements, setShowElements] = useState(false);
  const [blackListedCount, setBlackListedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false)

  const goBack = () => {
    controller.goBack();
  };

  useEffect(() => {
    getStorageItem("manual_time_picker").then((data) => {
      setTimePickerManualMode(!!data);
    });

    const userSettings = controller.state.settings;
    if (!userSettings) return;

    userSettings.autoTrackInterval =
      userSettings.autotrack_hours <= 0
        ? "never"
        : userSettings.autotrack_hours >= 24
        ? Math.round(userSettings.autotrack_hours / 24) +
          " day" +
          (userSettings.autotrack_hours >= 48 ? "s" : "")
        : userSettings.autotrack_hours + " hours";

    userSettings.notificationTime = convert24hToAMPM(
      userSettings.notification_time,
    );

    if (useDeviceTz == undefined) {
      setUseDeviceTz(
        !!userSettings.useDeviceTz || "Current" === userSettings.tz_override,
      );
    }

    setSettings(userSettings);
    setIsLoading(false);

    setBlackListedCount(
      (controller.state.settings?.blacklisted_elements || []).length,
    );
  }, [controller.state?.settings]);

  const updateSettings = (key, value) => {
    if (!key) return;

    let targetKey = key;
    let targetValue = value;
    if (key === "autoTrackInterval") {
      targetKey = "autotrack_hours";
      const arr = value.split(" ");
      targetValue =
        value.toLowerCase() !== "never"
          ? arr[0] * (arr[1].startsWith("day") ? 24 : 1)
          : value;
    } else if (key === "notificationTime") {
      targetKey = "notification_time";
    }

    setIsLoading(true)

    SettingsHelper.updatePreferences(
      controller,
      { ...settings, [targetKey]: targetValue },
      () => {
        controller.getItems();
      },
    );
  };

  const goToElementsPage = () => {
    setShowElements(true);
  };

  const updateTimePickerMode = (value) => {
    setTimePickerManualMode(value);
    setStorageItem("manual_time_picker", value);
  };

  return showElements ? (
    <View style={appStyles.container}>
      <BlacklistElements controller={controller} goBack={goBack} />
    </View>
  ) : (
    <View style={appStyles.container}>
      {isLoading && <LoadingIndicator  isLoading={true}/>}

      <IntervalDurationDialog
        onConfirm={(value) => updateSettings("interval_duration", value)}
        value={settings.interval_duration}
        isOpen={isIntervalDialogOpen}
        onClose={() => setIsIntervalDialogOpen(false)}
      />

      <TimezoneDialog
        onConfirm={(value) => updateSettings("tz_override", value)}
        value={settings.tz_override}
        isOpen={isTimezoneDialogOpen}
        onClose={() => setIsTimezoneDialogOpen(false)}
      />

      <TimePickerDialog
        value={settings.notificationTime}
        isOpen={isNotificationDialogOpen}
        minutesInterval={settings.interval_duration}
        format={"HH:mm"}
        onConfirm={(value) => updateSettings("notificationTime", value)}
        onClose={() => setIsNotificationDialogOpen(false)}
      />

      <AutoTrackPickerDialog
        value={settings.autoTrackInterval}
        isOpen={isAutoTrackDialogOpen}
        onConfirm={(value) => updateSettings("autoTrackInterval", value)}
        onClose={() => setIsAutoTrackDialogOpen(false)}
      />

      <Header
        title="Preferences"
        subTitle="Customize your experience"
        goBack={goBack}
        hideAvatar
      />

      <ScrollView style={[appStyles.scrollView, styles.scrollView]}>
        {/* ---------------------------- TIMEZONE ---------------------------- */}
        <View style={styles.row}>
          <Text style={styles.optionText}>Use Device Timezone</Text>
          <Switch
            thumbColor="#FFF"
            trackColor={{
              true: thumbColor,
              false: "#f3f2f1",
            }}
            onValueChange={() => {
              setUseDeviceTz(!useDeviceTz);
              updateSettings(
                "tz_override",
                useDeviceTz
                  ? "Current"
                  : settings.tz_override != null &&
                    settings.tz_override !== "Current"
                  ? settings.tz_override
                  : "America/Los_Angeles",
              );
            }}
            value={useDeviceTz}
          />
        </View>
        {!useDeviceTz ? (
          <View style={styles.row}>
            <View>
              <Text style={[styles.optionText]}>Time Zone</Text>
            </View>
            <TouchableOpacity onPress={() => setIsTimezoneDialogOpen(true)}>
              <Text style={[styles.optionValue]}>{settings.tz_override}</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {/* Timers */}
        {/* <View style={styles.row}>
          <Text style={styles.optionText}>Use timers</Text>
          <Switch
            thumbColor="#FFF"
            trackColor={{
              true: thumbColor,
              false: "#f3f2f1",
            }}
            onValueChange={(value) => updateSettings("use_timers", value)}
            value={settings.use_timers}
          />
        </View> */}
        {/* 24H - disabled till we have this
          <View style={styles.row}>
            <Text style={styles.optionText}>24h timer</Text>
            <Switch
              thumbColor={thumbColor}
              onValueChange={(value) => updateSettings("use24hFormat", value)}
              value={settings.use24hFormat}
            />
          </View>
          */}

        {/* ---------------------------- AUTOTRACK & PREDICTIONS ---------------------------- */}
        <View style={[styles.row, styles.borderTop]}>
          <Text style={styles.optionText}>Chunk Predictions</Text>
          <Switch
            thumbColor="#FFF"
            trackColor={{
              true: thumbColor,
              false: "#f3f2f1",
            }}
            onValueChange={(value) =>
              updateSettings("chunk_predictions", value)
            }
            value={!!+settings.chunk_predictions}
          />
        </View>
        <View style={[styles.row]}>
          <Text style={styles.optionText}>Autoconfirm Predictions</Text>
          <Switch
            thumbColor="#FFF"
            trackColor={{
              true: thumbColor,
              false: "#f3f2f1",
            }}
            onValueChange={(value) =>
              updateSettings("autoconfirm_predictions", value)
            }
            value={!!+settings.autoconfirm_predictions}
          />
        </View>
        <View style={[styles.row]}>
          <Text style={styles.optionText}>Autoconfirm Common Patterns</Text>
          <Switch
            thumbColor="#FFF"
            trackColor={{
              true: thumbColor,
              false: "#f3f2f1",
            }}
            onValueChange={(value) => updateSettings("autoconfirm_atus", value)}
            value={!!+settings.autoconfirm_atus}
          />
        </View>
        <View style={[styles.row]}>
          <Text style={styles.optionText}>Autotrack after</Text>
          <TouchableOpacity onPress={() => setIsAutoTrackDialogOpen(true)}>
            <Text style={styles.optionValue}>{settings.autoTrackInterval}</Text>
          </TouchableOpacity>
        </View>
        <View style={[styles.row, { justifyContent: "flex-end" }]}>
          <TouchableOpacity onPress={() => goToElementsPage()}>
            <Text style={styles.optionValue}>
              Elements blacklisted from Autotrack ({blackListedCount})
            </Text>
          </TouchableOpacity>
        </View>

        {/* ---------------------------- TIMEPICKER ---------------------------- */}
        <View style={[styles.row, styles.borderTop]}>
          <Text style={styles.optionText}>Default Time-Tracking Interval</Text>
          <TouchableOpacity onPress={() => setIsIntervalDialogOpen(true)}>
            <Text style={styles.optionValue}>
              {settings.interval_duration} min
            </Text>
          </TouchableOpacity>
        </View>
        <View style={[styles.row]}>
          <Text style={styles.optionText}>
            Default Time Picker to Manual Entry
          </Text>
          <Switch
            thumbColor="#FFF"
            trackColor={{
              true: thumbColor,
              false: "#f3f2f1",
            }}
            onValueChange={(value) => updateTimePickerMode(value)}
            value={timePickerManualMode}
          />
        </View>
        <View style={styles.row}>
          <Text style={styles.optionText}>Display Track Card in Home Feed</Text>
          <Switch
            thumbColor="#FFF"
            trackColor={{
              true: thumbColor,
              false: "#f3f2f1",
            }}
            onValueChange={(value) => updateSettings("show_home_card", value)}
            value={!!+settings.show_home_card}
          />
        </View>
        <View style={styles.row}>
          <Text style={styles.optionText}>Display Timer in Home Feed</Text>
          <Switch
            thumbColor="#FFF"
            trackColor={{
              true: thumbColor,
              false: "#f3f2f1",
            }}
            onValueChange={(value) => updateSettings("show_timer_card", value)}
            value={!!+settings.show_timer_card}
          />
        </View>
        <View style={[styles.row]}>
          <Text style={styles.optionText}>Track Work Time Only</Text>
          <Switch
            thumbColor="#FFF"
            trackColor={{
              true: thumbColor,
              false: "#f3f2f1",
            }}
            onValueChange={(value) => updateSettings("track_work_only", value)}
            value={!!+settings.track_work_only}
          />
        </View>
        <View style={[styles.row]}>
          <Text style={styles.optionText}>Vacation Mode</Text>
          <Switch
            thumbColor="#FFF"
            trackColor={{
              true: thumbColor,
              false: "#f3f2f1",
            }}
            onValueChange={(value) => updateSettings("vacation_mode", value)}
            value={!!+settings.vacation_mode}
          />
        </View>

        {/* ---------------------------- NOTIFICATIONS & MARKETING ---------------------------- */}
        <View style={[styles.row, styles.borderTop]}>
          <Text style={styles.optionText}>Receive notifications</Text>
          <Switch
            thumbColor="#FFF"
            trackColor={{
              true: thumbColor,
              false: "#f3f2f1",
            }}
            onValueChange={(value) =>
              updateSettings("enable_notifications", value)
            }
            value={settings.enable_notifications}
          />
        </View>
        {settings.enable_notifications ? (
          <View style={[styles.row]}>
            <Text style={[styles.optionText, styles.optionTextSmall]}>
              Notification time
            </Text>
            <TouchableOpacity onPress={() => setIsNotificationDialogOpen(true)}>
              <Text style={styles.optionValue}>
                {settings.notificationTime}
              </Text>
            </TouchableOpacity>
          </View>
        ) : null}

        {/* Marketing Emails */}
        <View style={styles.row}>
          <Text style={styles.optionText}>Receive Tips & Deals from Mana</Text>
          <Switch
            thumbColor="#FFF"
            trackColor={{
              true: thumbColor,
              false: "#f3f2f1",
            }}
            onValueChange={(value) =>
              updateSettings("marketing_email_flag", value)
            }
            value={!!+settings.marketing_email_flag}
          />
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    paddingBottom: 130,
    paddingHorizontal: 10,
    paddingTop: 5,
    backgroundColor: "#FFF",
    marginTop: 0,
  },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    height: 40,
    paddingHorizontal: 8,
  },
  borderTop: {
    borderTopColor: "#f5f5f5",
    borderTopWidth: 1,
  },
  optionText: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-Regular",
    }),
    fontWeight: "300",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.5,
    color: "#2D3142",
  },
  optionTextSmall: {
    fontSize: 12,
    lineHeight: 14,
    paddingLeft: 16,
  },
  optionValue: {
    fontFamily: Platform.select({
      default: "Montserrat",
      android: "Montserrat-SemiBold",
    }),
    fontWeight: "600",
    fontSize: 14,
    lineHeight: 17,
    letterSpacing: 0.175,
    color: AppTheme.DEFAULT_COLOR,
    textAlign: "center",
  },
});
